import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { Button } from './Button.tsx';

export interface FilterChip {
  /** Stable id, for example 'status' or 'where:2'. */
  id: string;
  /** What is filtered: "Status", "Machine". Omit for a chip that reads on its own ("failed today"). */
  field?: string;
  value: string;
  icon?: ReactNode;
  onRemove: () => void;
}

export interface FilterChipsProps {
  chips: FilterChip[];
  onClearAll?: () => void;
  /** Leading content, for example the saved views menu. */
  leading?: ReactNode;
  /** Trailing content, for example the density toggle. */
  trailing?: ReactNode;
  className?: string;
}

/**
 * Active filters as removable chips (DesignSystem 3.4). Pages mirror every chip in the URL,
 * so the row of chips is also exactly what a shared link shows.
 */
export function FilterChips({ chips, onClearAll, leading, trailing, className }: FilterChipsProps) {
  if (!chips.length && !leading && !trailing) return null;
  return (
    <div className={cn('flex min-h-control flex-wrap items-center gap-1.5', className)}>
      {leading}
      {chips.length > 0 && (
        <ul className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
          {chips.map((c) => (
            <li key={c.id}>
              <span
                data-chip=""
                className="inline-flex h-control-xs items-center gap-1 rounded-control border border-border bg-control pl-2 pr-0.5 text-caption text-fg shadow-control [&_svg]:size-3"
              >
                {c.icon && <span className="flex text-fg-subtle">{c.icon}</span>}
                {c.field && <span className="text-fg-subtle">{c.field}</span>}
                <span className="max-w-(--popover-min-width) truncate font-medium">{c.value}</span>
                <button
                  type="button"
                  onClick={c.onRemove}
                  aria-label={`Remove filter ${c.field ? `${c.field} ` : ''}${c.value}`}
                  className="ml-0.5 inline-flex size-5 items-center justify-center rounded-control text-fg-subtle transition-colors duration-(--duration-fast) ease-standard hover:bg-fg/6 hover:text-fg"
                >
                  <X aria-hidden />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {chips.length > 1 && onClearAll && (
        <Button size="sm" variant="ghost" className="h-control-xs px-2 text-caption" onClick={onClearAll}>
          Clear all
        </Button>
      )}
      {trailing && <div className="ml-auto flex items-center gap-2">{trailing}</div>}
    </div>
  );
}
